import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Tooltip,
} from '@mui/material';
import _take from 'lodash/take';
import { createContext, forwardRef, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Fade } from 'react-bootstrap';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import useEventCallback from 'src/app/common/hook/useEventCallback';
import { normalizeText } from 'src/app/modules/OpmPd/utils/commonHelper';
import useStyles from './TableHeaderFilterLabel.styles';

type PopperFilterProps = {
  options: string[];
  selectedOptions: string[];
  onOptionToggle: (option: string) => void;
};

export type TableHeaderFilterLabelProps = {
  align?: 'left' | 'center' | 'right';
  label?: string;
  options: string[];
  selectedOptions: string[];
  onSelectionChange?: (selectedOptions: string[]) => void;
};

const OPTION_ITEM_HEIGHT = 44;
const OPTION_ITEM_MAX_COUNT = 5;

type OptionRowData = {
  option: string;
};

const PopperFilterContext = createContext<{
  changeSize: (index: number, size: number) => void;
  selectedOptions: string[];
  onOptionToggle: (option: string) => void;
} | null>(null);

const OptionRow = ({ index, style, data }: ListChildComponentProps<OptionRowData[]>) => {
  const option = data[index].option;
  const { changeSize, selectedOptions, onOptionToggle } = useContext(PopperFilterContext)!;
  const rootElRef = useRef<HTMLLIElement>(null);

  const { classes } = useStyles();

  useLayoutEffect(() => {
    const rootEl = rootElRef.current;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { height } = entry.contentRect;
        changeSize(index, height);
      }
    });
    if (rootEl) {
      resizeObserver.observe(rootEl);
    }
    return () => {
      if (rootEl) {
        resizeObserver.unobserve(rootEl);
      }
    };
  }, []);

  const listItemStyle = {
    ...style,
    height: 'auto',
  };

  return (
    <ListItem disablePadding style={listItemStyle} ref={rootElRef}>
      <ListItemButton className={classes.filterPopperPaperContentListItemButton} onClick={() => onOptionToggle(option)}>
        <Checkbox
          edge="start"
          checked={selectedOptions.includes(option)}
          tabIndex={-1}
          disableRipple
          sx={{ paddingTop: 0, paddingBottom: 0 }}
        />
        <ListItemText primary={option} />
      </ListItemButton>
    </ListItem>
  );
};

const renderItem = (props: { style: React.CSSProperties; index: number; data: OptionRowData[] }) => {
  const { style, index, data } = props;
  return <OptionRow style={style} index={index} data={data} />;
};

const ItemList = forwardRef((props: any, ref: React.ForwardedRef<any>) => {
  const { children, ...rest } = props;

  return (
    <List ref={ref} {...rest} disablePadding>
      {children}
    </List>
  );
});

const OuterElementType = forwardRef((props: any, ref: React.ForwardedRef<any>) => {
  const { className, ...rest } = props;
  const { classes, cx } = useStyles();
  return <Box ref={ref} {...rest} className={cx(classes.filterPopperPaperContentList, className)} />;
});

const PopperFilter = (props: PopperFilterProps) => {
  const { options: controlledOptions, selectedOptions, onOptionToggle } = props;
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const [keyword, setKeyword] = useState('');

  const { classes } = useStyles();

  const indexToItemSizeMapRef = useRef<Record<number, number>>({});
  const variableSizeListRef = useRef<VariableSizeList>(null);
  const variableSizeListOuterRef = useRef<HTMLDivElement>(null);

  const items = useMemo(() => {
    const normalizedKeyword = normalizeText(keyword);
    return controlledOptions
      .filter((option) => normalizeText(option).includes(normalizedKeyword))
      .map((option) => ({
        option,
      }));
  }, [keyword, controlledOptions]);
  const itemCount = items.length;

  const [variableSizeListHeight, setVariableSizeListHeight] = useState(() => {
    return OPTION_ITEM_HEIGHT * (itemCount > OPTION_ITEM_MAX_COUNT ? OPTION_ITEM_MAX_COUNT : itemCount);
  });

  const getItemSize = (index: number) => {
    return indexToItemSizeMapRef.current[index] || OPTION_ITEM_HEIGHT;
  };

  const updateVariableSizeListHeight = useEventCallback(() => {
    const newVariableSizeListHeight = items.reduce((variableSizeListHeight, _, index) => {
      const itemSize = getItemSize(index);
      return itemSize + variableSizeListHeight;
    }, 0);
    const maxHeight =
      getVariableSizeListHeight() ||
      OPTION_ITEM_HEIGHT * (itemCount > OPTION_ITEM_MAX_COUNT ? OPTION_ITEM_MAX_COUNT : itemCount);
    setVariableSizeListHeight(newVariableSizeListHeight > maxHeight ? maxHeight : newVariableSizeListHeight);
  });

  const changeSize = (index: number, size: number) => {
    indexToItemSizeMapRef.current[index] = size;
    updateVariableSizeListHeight();
    variableSizeListRef.current?.resetAfterIndex(index);
  };

  const getVariableSizeListHeight = () => {
    if (itemCount > OPTION_ITEM_MAX_COUNT) {
      return _take(items, OPTION_ITEM_MAX_COUNT)
        .map((_, index) => getItemSize(index))
        .reduce((sum, s) => sum + s, 0);
    }
    return items.map((_, index) => getItemSize(index)).reduce((sum, s) => sum + s, 0);
  };

  useEffect(() => {
    updateVariableSizeListHeight();
  }, [items.length]);

  return (
    <PopperFilterContext.Provider value={{ changeSize, selectedOptions, onOptionToggle }}>
      <div className={classes.filterPopperPaperTitle}>
        <TextField
          label={Translation('opm_pd.filter')}
          variant="outlined"
          size="small"
          focused
          fullWidth
          onChange={(e) => setKeyword(e.target.value)}
        />
      </div>
      <div className={classes.filterPopperPaperContent}>
        <VariableSizeList
          itemData={items as any}
          height={variableSizeListHeight}
          width="100%"
          itemCount={itemCount}
          itemSize={getItemSize}
          ref={variableSizeListRef}
          layout="vertical"
          innerElementType={ItemList}
          outerElementType={OuterElementType}
          outerRef={variableSizeListOuterRef}
          overscanCount={5}
        >
          {renderItem}
        </VariableSizeList>
      </div>
    </PopperFilterContext.Provider>
  );
};

const TableHeaderFilterLabel = (props: TableHeaderFilterLabelProps) => {
  const { label, options, selectedOptions, align, onSelectionChange } = props;
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pendingOptions, setPendingOptions] = useState<string[]>(selectedOptions);

  const { classes, theme } = useStyles();

  const handlePopperToggle = (event: React.MouseEvent<HTMLElement>) => {
    if (!anchorEl) {
      setPendingOptions(selectedOptions);
    }
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handlePopperClose = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (anchorEl === (event.target as HTMLElement) || anchorEl?.contains(event.target as HTMLElement)) {
      return;
    }
    setAnchorEl(null);
  };

  const handleApply = () => {
    onSelectionChange?.(pendingOptions);
    setAnchorEl(null);
  };

  const handleClear = () => {
    setPendingOptions([]);
  };

  const activeFilterCount = selectedOptions.length;

  useEffect(() => {
    setPendingOptions(selectedOptions);
  }, [selectedOptions]);

  return (
    <Box display="flex" alignItems="center" justifyContent={align} gap={0.5}>
      <Box component="span">{label}</Box>
      <IconButton size="small" onClick={handlePopperToggle}>
        <Badge badgeContent={activeFilterCount} color="error" max={999}>
          <FilterListIcon fontSize="small" sx={{ color: 'white' }} />
        </Badge>
      </IconButton>
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-end"
        transition
        keepMounted
        modifiers={[
          {
            name: 'flip',
            enabled: true,
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
            },
          },
        ]}
        style={{ zIndex: theme.zIndex.tooltip }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <div>
              <ClickAwayListener onClickAway={handlePopperClose as any}>
                <Paper className={classes.filterPopperPaper} elevation={3}>
                  <PopperFilter
                    options={options}
                    selectedOptions={pendingOptions}
                    onOptionToggle={(option) => {
                      setPendingOptions((prev) =>
                        prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option],
                      );
                    }}
                  />
                  <div className={classes.filterPopperPaperActions}>
                    <Button onClick={handleClear}>{Translation('opm_pd.clear')}</Button>
                    <Button variant="contained" color="secondary" onClick={handleApply}>
                      {Translation('opm_pd.apply')}
                    </Button>
                  </div>
                </Paper>
              </ClickAwayListener>
            </div>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};

export default TableHeaderFilterLabel;
